<template>
  <div class="pdf-viewer" :class="{ 'pdf-viewer--show-forms': showFormFields }" ref="pdfViewer" id="viewer-container">
    <PdfFindBar
      :match-count="findMatchCount"
      :current-match="findCurrentMatch"
      @previous="findPrevious"
      @next="findNext"
      @close="clearPdfFind"
    />
    <div id="viewer" ref="viewer" class="pdfViewer"></div>
  </div>
</template>

<script>
import { bus } from '@/main';
import axios from 'axios';
import 'pdfjs-dist/build/pdf.mjs'; // Must be imported before pdf_viewer.mjs to set globalThis.pdfjsLib
import * as pdfjsViewer from 'pdfjs-dist/web/pdf_viewer.mjs';
import { mapActions, mapGetters } from 'vuex';

const CancelToken = axios.CancelToken;

import TextAnnotatorMixin from '@/mixins/text-annotator-mixin';
import SearchTextPdf from '@/mixins/search-text-pdf';
import { getTextLayerElements, highlightMatchSpan } from '@/util/pdf-viewer-util';
import PdfFindBar from '@/components/pdf-viewer/PdfFindBar';
import { canCopyDocument } from '@/util/document-permissions-util';
import { viewport } from '@/util/viewport';

export default {
  name: 'PDFViewer',
  components: { PdfFindBar },
  mixins: [TextAnnotatorMixin, SearchTextPdf],
  props: {
    pdfDocument: {
      type: Object,
      required: true,
    },
    // Show pdf.js's AcroForm/annotation layer (hidden by default so the app's
    // own annotation overlay is the only thing rendered).
    showFormFields: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      nativeViewer: null,
      pdfEventBus: null,
      pdfLinkService: null,
      pagesReady: false,
      pendingActions: [],
      syncingScale: false,
      scrollSyncTimer: null,
      findMatchCount: 0,
      findCurrentMatch: 0,
      // Extraction search state
      extractionSearchInProgress: false,
      extractionSearchActive: false,
      extractionPages: [],
      extractionPageIndex: 0,
      extractionPageOccurrence: 0,
    };
  },
  computed: {
    ...mapGetters('pdfData', ['pdfScale', 'pdf']),
    ...mapGetters('opportunityDocuments', ['opportunityDocument']),
    ...mapGetters('opportunityDocumentsPdfEngine', ['matchesByKeyword', 'searchResult']),
    ...mapGetters('documentKeywords', ['categories']),
    ...mapGetters('opportunityKeywordSearch', ['selectedKeyword', 'keywordLocations']),
  },
  methods: {
    listen(event, handler) {
      bus.$on(event, handler);
      this.busListeners.push([event, handler]);
    },
    ...mapActions('currentDocument', ['setCurrentPageNumber']),
    ...mapActions('pdfData', ['setPdfScale']),
    ...mapActions('opportunityDocumentsPdfEngine', ['searchContent']),
    ...mapActions('opportunityKeywordSearch', [
      'findManualKeywordLocations',
      'setSelectedKeyword',
      'resetSelectedKeyword',
    ]),

    isValidPageNumber(pageNumber) {
      if (!this.nativeViewer) return false;
      // If pagesCount is 0, PDF is not loaded yet - don't show warning
      if (this.nativeViewer.pagesCount === 0) return false;
      const isValid = pageNumber >= 1 && pageNumber <= this.nativeViewer.pagesCount;
      if (!isValid) {
        console.warn(`Invalid pageNumber ${pageNumber}, pagesCount is ${this.nativeViewer.pagesCount}`);
      }
      return isValid;
    },

    queueAction(actionFn) {
      if (this.pagesReady && this.nativeViewer && this.nativeViewer.pagesCount > 0) {
        actionFn();
      } else {
        this.pendingActions.push(actionFn);
      }
    },

    processPendingActions() {
      while (this.pendingActions.length > 0) {
        const actionFn = this.pendingActions.shift();
        actionFn();
      }
    },

    onPagesReady() {
      if (this.pagesReady) return;
      this.$nextTick(() => {
        this.pagesReady = true;
        this.processPendingActions();
        bus.$emit('pdf-viewer-ready');
      });
    },

    onScrollEnd() {
      this.$emit('scroll');
      clearTimeout(this.scrollSyncTimer);
      this.scrollSyncTimer = setTimeout(() => {
        if (this.nativeViewer && this.nativeViewer.currentPageNumber) {
          this.setCurrentPageNumber(this.nativeViewer.currentPageNumber);
        }
      }, 150);
    },

    centerHorizontally() {
      const container = this.$refs.pdfViewer;
      if (!container || !this.nativeViewer) return;

      const pageView = this.nativeViewer.getPageView(this.nativeViewer.currentPageNumber - 1);
      if (!pageView || !pageView.div) return;

      const pageDiv = pageView.div;
      const pageWidth = pageDiv.offsetWidth;
      const containerWidth = container.clientWidth;

      if (pageWidth > containerWidth) {
        const pageLeft = pageDiv.offsetLeft;
        container.scrollLeft = pageLeft + (pageWidth - containerWidth) / 2;
      } else {
        container.scrollLeft = 0;
      }
    },

    getMaxCanvasPixels(scale) {
      const BASE_PIXELS = 33_554_432;
      const zoomFactor = Math.max(1, (scale || 100) / 100);
      return Math.round(BASE_PIXELS * zoomFactor * zoomFactor);
    },

    updateMaxCanvasPixels(scale) {
      if (!this.nativeViewer) return;
      const maxPixels = this.getMaxCanvasPixels(scale);
      this.nativeViewer.maxCanvasPixels = maxPixels;
      const numPages = this.nativeViewer.pagesCount;
      for (let i = 0; i < numPages; i++) {
        const pageView = this.nativeViewer.getPageView(i);
        if (pageView) {
          pageView.maxCanvasPixels = maxPixels;
        }
      }
    },

    applyZoomMode(mode) {
      if (!this.nativeViewer) return;
      this.nativeViewer.currentScaleValue = mode;
      this.$nextTick(() => {
        if (this.nativeViewer) {
          const actualScale = Math.round(this.nativeViewer.currentScale * 100);
          this.syncingScale = true;
          this.setPdfScale(actualScale);
          this.$nextTick(() => {
            this.syncingScale = false;
          });
        }
      });
    },

    onNativePageRendered(evt) {
      const pageNumber = evt.pageNumber;
      const pageView = this.nativeViewer.getPageView(pageNumber - 1);
      if (pageView && pageView.div) {
        pageView.div.id = `page-${pageNumber}`;
        pageView.div.classList.add('pdf-viewer__page', 'rendered');
      }
    },

    onNativeTextLayerRendered(evt) {
      const pageNumber = evt.pageNumber;
      const pageView = this.nativeViewer.getPageView(pageNumber - 1);
      if (pageView && pageView.div) {
        const textLayer = pageView.div.querySelector('.textLayer');
        if (textLayer) {
          textLayer.id = `page-${pageNumber}-text-layer`;
        }
      }

      // Emit page-rendered BEFORE keyword highlights so annotation XPaths
      // resolve against a clean DOM (keyword wrapping splits text nodes)
      this.$emit('page-rendered', pageNumber);

      this.renderKeywordsHighlightLayer(pageNumber);
    },

    renderKeywordsHighlightLayer(pageNumber) {
      if (this.matchesByKeyword.length === 0) {
        const unwatch = this.$watch(
          'matchesByKeyword',
          (newMatchesByKeyword) => {
            if (newMatchesByKeyword.length > 0) {
              unwatch();
              this.renderKeywordsHighlightLayer(pageNumber);
            }
          },
          { immediate: true },
        );
      }
      // get all matchesByKeyword for the current document and remove any other document matches
      const currentDocKeywordMatches = this.matchesByKeyword.map((keywordMatches) => {
        const currentDocMatches = Object.entries(keywordMatches.matchesByDoc)
          .filter(([docId]) => docId === this.opportunityDocument.id)
          .reduce((acc, [docId, matches]) => {
            return {
              ...acc,
              [docId]: matches,
            };
          }, {});
        return {
          ...keywordMatches,
          matchesByDoc: currentDocMatches,
        };
      });
      if (currentDocKeywordMatches.length > 0) {
        this.highlightKeywordMatches(pageNumber, currentDocKeywordMatches);
      }
      if (this.selectedKeyword && this.selectedKeyword.id === null) {
        this.highlightManualKeywordInCurrentPage(pageNumber);
      }
    },
    highlightKeywordMatches(pageNumber, currentDocKeywordMatches) {
      const pageElements = getTextLayerElements(pageNumber);
      if (!pageElements.length) return;
      pageElements.forEach((pageElement, i) => {
        pageElement.setAttribute('data-pos', `${pageNumber}_${i}`);
      });
      currentDocKeywordMatches.forEach((keywordMatches) => {
        const category = this.categories.find((cat) => cat.id === keywordMatches.keyword.categoryId);
        const matches = Object.values(keywordMatches.matchesByDoc).flat();
        let pageOccurrence = 0;
        matches.forEach((match) => {
          if (match.start.page !== pageNumber) {
            return;
          }

          highlightMatchSpan(
            pageElements,
            match.start.element,
            match,
            `keyword-${keywordMatches.keyword.id}-${pageOccurrence}`,
            category.colorCode,
          );
          pageOccurrence++;
        });
      });
    },
    highlightManualKeywordInCurrentPage(pageNumber) {
      const color = this.extractionSearchActive ? '#80a2f7' : undefined;
      this.highlightSearchResults(pageNumber, this.searchResult, this.opportunityDocument.id, color);
    },
    highlightManualSearch() {
      if (!this.nativeViewer) return;
      const numPages = this.pdfDocument.numPages;
      for (let i = 1; i <= numPages; i++) {
        const pageView = this.nativeViewer.getPageView(i - 1);
        if (pageView && pageView.renderingState === 3) {
          this.highlightManualKeywordInCurrentPage(i);
        }
      }
    },

    findNext() {
      if (this.extractionSearchActive) {
        this._nextExtractionOccurrence();
      }
    },
    findPrevious() {
      if (this.extractionSearchActive) {
        this._previousExtractionOccurrence();
      }
    },
    clearPdfFind() {
      if (this.extractionSearchActive) {
        this._clearExtractionSearch();
      }
      this.findMatchCount = 0;
      this.findCurrentMatch = 0;
    },

    // --- Extraction search via pdf-engine ---

    async searchExtractionSource(queries, targetPage = null) {
      // Cancel any in-flight search so a rapid succession of clicks resolves
      // to the LAST click. Without this, the first click holds the guard and
      // subsequent clicks are silently ignored, and the PDF never highlights
      // the extraction the user actually wanted.
      if (this._extractionCancelSource) {
        this._extractionCancelSource.cancel('superseded by new search');
      }
      const cancelSource = CancelToken.source();
      this._extractionCancelSource = cancelSource;

      this.extractionSearchInProgress = true;

      // Normalize: accept a single query string or an array
      const queryList = Array.isArray(queries) ? queries : [queries];

      try {
        this.clearPdfFind();
        this.extractionSearchActive = true;

        const opportunityId = this.opportunityDocument && this.opportunityDocument.opportunityId;
        if (!opportunityId) {
          this.extractionSearchActive = false;
          return;
        }

        if (targetPage) {
          this.nativeViewer.scrollPageIntoView({ pageNumber: targetPage });
          requestAnimationFrame(() => {
            this.centerHorizontally();
          });
        }

        // Try each query until one produces matches
        let matchedQuery = null;
        let totalCount = 0;
        let docLocation = null;

        for (const query of queryList) {
          try {
            await this.searchContent({ opportunityId, query, cancelToken: cancelSource.token });
          } catch (err) {
            if (axios.isCancel(err)) return; // superseded — leave state alone
            continue;
          }

          // Check again in case cancellation happened between requests
          if (this._extractionCancelSource !== cancelSource) return;

          await this.findManualKeywordLocations({ searchResult: this.searchResult });

          const docId = this.opportunityDocument.id;
          docLocation = this.keywordLocations.find((loc) => loc.docId === docId);
          totalCount = docLocation ? docLocation.count : 0;

          if (totalCount > 0) {
            matchedQuery = query;
            break;
          }
        }

        // Final check — if we were superseded between the last await and now,
        // bail so we don't clobber the state of the winning search.
        if (this._extractionCancelSource !== cancelSource) return;

        if (!matchedQuery || totalCount === 0) {
          if (targetPage) {
            this.$toast.show(this.$t('pdf-viewer.extraction-highlight-failed'));
          } else {
            this.$toast.show(this.$t('pdf-viewer.no-match-found'));
          }
          this.extractionSearchActive = false;
          return;
        }

        this.extractionPages = docLocation.pages;

        let startIndex = 0;
        if (targetPage) {
          const pageIdx = this.extractionPages.indexOf(targetPage);
          if (pageIdx !== -1) {
            startIndex = pageIdx;
          }
        }

        this.extractionPageIndex = startIndex;
        this.extractionPageOccurrence = 0;

        this.resetSelectedKeyword();
        const sel = window.getSelection();
        if (sel) sel.removeAllRanges();

        await this.setSelectedKeyword({
          keyword: { id: null, value: matchedQuery },
          occurrences: totalCount,
        });
        this.highlightManualSearch();

        this.findMatchCount = totalCount;
        this.findCurrentMatch = startIndex + 1;

        const page = this.extractionPages[startIndex];
        this._scrollToExtractionOccurrence(page, 0);
      } finally {
        // Only clear the flag if we're still the winning search. A superseded
        // search must not reset these, otherwise the winning search's state
        // gets clobbered.
        if (this._extractionCancelSource === cancelSource) {
          this._extractionCancelSource = null;
          this.extractionSearchInProgress = false;
          bus.$emit('extraction-search-done');
        }
      }
    },

    _scrollToExtractionOccurrence(pageNumber, pageOccurrence) {
      const scrollAndHighlight = () => {
        this._lowlightAllKeywords();
        const pageElement = document.getElementById(`page-${pageNumber}`);
        const keywordDivs = pageElement ? pageElement.querySelectorAll(`.keyword-null-${pageOccurrence}`) : [];
        if (keywordDivs.length) {
          keywordDivs.forEach((el) => {
            el.style.filter = 'brightness(1)';
          });
          const container = keywordDivs[0].closest('.pdf-viewer');
          if (container) {
            const elementRect = keywordDivs[0].getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            container.scrollTop +=
              elementRect.top - containerRect.top - container.clientHeight / 2 + elementRect.height / 2;
          }
        }
      };

      const textLayer = document.getElementById(`page-${pageNumber}-text-layer`);
      if (textLayer && textLayer.querySelector('keyword')) {
        requestAnimationFrame(scrollAndHighlight);
      } else {
        bus.$emit('scroll-to-keyword-page', {
          pageNumber,
          callback: scrollAndHighlight,
        });
      }
    },

    _nextExtractionOccurrence() {
      if (this.extractionPageIndex >= this.extractionPages.length - 1) {
        return; // Already at last occurrence
      }

      const currentPage = this.extractionPages[this.extractionPageIndex];
      this.extractionPageIndex++;
      const nextPage = this.extractionPages[this.extractionPageIndex];

      // Same page → increment page occurrence, different page → reset to 0
      this.extractionPageOccurrence = nextPage === currentPage ? this.extractionPageOccurrence + 1 : 0;

      this.findCurrentMatch++;
      this._scrollToExtractionOccurrence(nextPage, this.extractionPageOccurrence);
    },

    _previousExtractionOccurrence() {
      if (this.extractionPageIndex <= 0) {
        return; // Already at first occurrence
      }

      const currentPage = this.extractionPages[this.extractionPageIndex];
      this.extractionPageIndex--;
      const prevPage = this.extractionPages[this.extractionPageIndex];

      if (prevPage === currentPage) {
        this.extractionPageOccurrence--;
      } else {
        // Find the largest occurrence index on the previous page
        this.extractionPageOccurrence = this._getLargestOccurrenceOnPage(prevPage);
      }

      this.findCurrentMatch--;
      this._scrollToExtractionOccurrence(prevPage, this.extractionPageOccurrence);
    },

    _getLargestOccurrenceOnPage(pageNumber) {
      const pageElement = document.getElementById(`page-${pageNumber}`);
      if (!pageElement) return 0;
      const kwNodes = pageElement.querySelectorAll('[class*="keyword-null-"]');
      if (!kwNodes.length) return 0;
      const indexes = [];
      kwNodes.forEach((node) => {
        const kwClass = [...node.classList].find((c) => c.startsWith('keyword-null-'));
        if (kwClass) indexes.push(parseInt(kwClass.split('-').pop(), 10));
      });
      return indexes.length ? Math.max(...indexes) : 0;
    },

    _lowlightAllKeywords() {
      const keywordDivs = document.querySelectorAll('keyword');
      keywordDivs.forEach((div) => {
        div.style.filter = 'brightness(0.3)';
      });
    },

    _clearExtractionSearch() {
      this.resetSelectedKeyword();
      this.extractionSearchActive = false;
      this.extractionPages = [];
      this.extractionPageIndex = 0;
      this.extractionPageOccurrence = 0;
    },
    // Text currently selected inside the PDF viewer, or '' when the selection lives elsewhere
    // (keyword search input, side panels), so those keep their native copy behaviour.
    getViewerSelectedText() {
      const selection = window.getSelection();
      const pdfViewerElement = this.$refs.pdfViewer;
      if (!selection || !pdfViewerElement || !pdfViewerElement.contains(selection.anchorNode)) return '';
      return selection.toString();
    },
    handleCopy(event) {
      const isCopyShortcut = (event.ctrlKey || event.metaKey) && event.key === 'c';
      if (!isCopyShortcut) return;

      const selectedText = this.getViewerSelectedText();
      // When copying is restricted, handleNativeCopy blocks the native copy and shows the toast.
      if (!selectedText || !canCopyDocument(this.opportunityDocument)) return;

      navigator.clipboard.writeText(selectedText);
      this.$toast.success(this.$t('page.analysis.text-selection.copy.success'));
    },
    // Fires for Ctrl/Cmd+C, the context menu and the browser Edit menu alike. Registered in the capture
    // phase because pdf.js's text layer has its own `copy` handler that writes the selection to
    // `clipboardData` and stops propagation; stopping here keeps that handler from running at all.
    handleNativeCopy(event) {
      if (!this.getViewerSelectedText() || canCopyDocument(this.opportunityDocument)) return;
      event.preventDefault();
      event.stopPropagation();
      this.$toast.error(this.$t('page.analysis.text-selection.copy.not-allowed'));
    },
  },
  mounted() {
    this.busListeners = [];
    const eventBus = new pdfjsViewer.EventBus();
    this.pdfEventBus = eventBus;

    this.pdfLinkService = new pdfjsViewer.PDFLinkService({
      eventBus: eventBus,
    });

    this.nativeViewer = new pdfjsViewer.PDFViewer({
      container: this.$refs.pdfViewer,
      eventBus: eventBus,
      textLayerMode: 1,
      removePageBorders: true,
      maxCanvasPixels: this.getMaxCanvasPixels(this.pdfScale),
      linkService: this.pdfLinkService,
    });

    this.pdfLinkService.setViewer(this.nativeViewer);
    this.nativeViewer.setDocument(this.pdfDocument);
    this.pdfLinkService.setDocument(this.pdfDocument);

    this.pdfEventBus.on('pagesinit', () => {
      // Phones and tablets show the page across the screen, to read it; larger screens, all of it.
      this.applyZoomMode(['phone', 'tablet'].includes(viewport.size) ? 'page-width' : 'page-fit');
      this.onPagesReady();
    });

    // pagesinit only fires on first load; pagesloaded fires on every setDocument() call
    this.pdfEventBus.on('pagesloaded', () => {
      this.onPagesReady();
    });

    this.listen('set-pdf-zoom-mode', (mode) => {
      this.applyZoomMode(mode);
    });

    this.pdfEventBus.on('pagerendered', (evt) => {
      this.onNativePageRendered(evt);
    });

    this.pdfEventBus.on('textlayerrendered', (evt) => {
      this.onNativeTextLayerRendered(evt);
    });

    this.pdfEventBus.on('pagechanging', (evt) => {
      this.setCurrentPageNumber(evt.pageNumber);
    });

    this.$refs.pdfViewer.addEventListener('scroll', this.onScrollEnd);

    this.listen('scroll-to-page', (pageNumber) => {
      this.queueAction(() => {
        if (!this.isValidPageNumber(pageNumber)) return;
        this.nativeViewer.scrollPageIntoView({ pageNumber });
        requestAnimationFrame(() => {
          this.centerHorizontally();
        });
      });
    });
    this.listen('render-annotation-page', (annotation) => {
      this.queueAction(() => {
        if (!this.isValidPageNumber(annotation.startPage)) return;
        // Skip scroll if the annotation page is already visible in the viewport
        const pageEl = document.getElementById(`page-${annotation.startPage}`);
        if (pageEl) {
          const container = this.$refs.pdfViewer;
          const pageRect = pageEl.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          if (pageRect.top < containerRect.bottom && pageRect.bottom > containerRect.top) {
            return;
          }
        }
        this.nativeViewer.scrollPageIntoView({ pageNumber: annotation.startPage });
      });
    });
    this.listen('scroll-to-keyword-page', ({ pageNumber, callback }) => {
      this.queueAction(() => {
        if (!this.isValidPageNumber(pageNumber)) return;
        this.nativeViewer.scrollPageIntoView({ pageNumber });

        // Wrap callback to force pdfjs to render any newly visible pages
        // after the keyword scrollIntoView repositions the viewport
        const wrappedCallback = () => {
          if (callback) callback();
          requestAnimationFrame(() => {
            this.nativeViewer.forceRendering();
          });
        };

        const textLayer = document.getElementById(`page-${pageNumber}-text-layer`);
        if (textLayer && textLayer.querySelector('keyword')) {
          requestAnimationFrame(() => {
            wrappedCallback();
          });
          return;
        }

        let called = false;
        const onTextLayerRendered = (evt) => {
          if (evt.pageNumber === pageNumber && !called) {
            called = true;
            this.pdfEventBus.off('textlayerrendered', onTextLayerRendered);
            requestAnimationFrame(() => {
              wrappedCallback();
            });
          }
        };
        this.pdfEventBus.on('textlayerrendered', onTextLayerRendered);

        setTimeout(() => {
          if (!called) {
            called = true;
            this.pdfEventBus.off('textlayerrendered', onTextLayerRendered);
            wrappedCallback();
          }
        }, 3000);
      });
    });
    this.listen('re-render-keywords', (pages) => {
      pages.forEach((page) => this.renderKeywordsHighlightLayer(page));
    });
    this.listen('manual-kw-search', () => {
      this.highlightManualSearch();
    });
    this.listen('search-extraction-source', ({ query, queries, targetPage }) => {
      this.queueAction(() => this.searchExtractionSource(queries || query, targetPage));
    });
    this.listen('clear-pdf-find', () => {
      this.clearPdfFind();
    });

    document.addEventListener('keydown', this.handleCopy);
    document.addEventListener('copy', this.handleNativeCopy, { capture: true });
  },
  beforeDestroy() {
    if (this.nativeViewer) {
      this.nativeViewer.setDocument(null);
    }

    // Its own listeners only: the analysis page listens to some of these events too.
    this.busListeners.forEach(([event, handler]) => bus.$off(event, handler));

    if (this.extractionSearchActive) {
      this._clearExtractionSearch();
    }

    if (this._extractionCancelSource) {
      this._extractionCancelSource.cancel('component destroyed');
      this._extractionCancelSource = null;
    }

    if (this.$refs.pdfViewer) {
      this.$refs.pdfViewer.removeEventListener('scroll', this.onScrollEnd);
    }
    clearTimeout(this.scrollSyncTimer);
    document.removeEventListener('keydown', this.handleCopy);
    document.removeEventListener('copy', this.handleNativeCopy, { capture: true });
  },
  watch: {
    pdfScale(newScale) {
      if (this.nativeViewer && !this.syncingScale && this.pagesReady) {
        this.updateMaxCanvasPixels(newScale);
        this.nativeViewer.currentScaleValue = newScale / 100;
        requestAnimationFrame(() => {
          this.centerHorizontally();
        });
      }
    },
    pdf(newPdf) {
      if (this.nativeViewer && newPdf) {
        this.pagesReady = false;
        this.nativeViewer.setDocument(newPdf);
        if (this.pdfLinkService) this.pdfLinkService.setDocument(newPdf);
      }
    },
    selectedKeyword(newVal) {
      // If someone else set a keyword (e.g. keyword panel), clear extraction search state
      if (this.extractionSearchActive && newVal && newVal.id !== null) {
        this.extractionSearchActive = false;
        this.extractionPages = [];
        this.extractionPageIndex = 0;
        this.extractionPageOccurrence = 0;
        this.findMatchCount = 0;
        this.findCurrentMatch = 0;
      }
    },
  },
};
</script>

<style lang="scss">
// Minimal pdfjs CSS — only what's needed for rendering (synced with pdfjs-dist v5.5.207).
// We do NOT import pdf_viewer.css because it leaks global styles
// (:root { color-scheme }, .sidebar, .dialog, etc.) into the whole app.

.pdfViewer {
  --scale-factor: 1;
  --page-bg-color: unset;
  --pdfViewer-padding-bottom: 0;
  --page-margin: 1px auto -8px;
  --page-border: 9px solid transparent;
}

.pdfViewer .canvasWrapper {
  overflow: hidden;
  width: 100%;
  height: 100%;
}

.pdfViewer .canvasWrapper canvas {
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  display: block;
  width: 100%;
  height: 100%;
}

.pdfViewer .page {
  --user-unit: 1;
  --total-scale-factor: calc(var(--scale-factor) * var(--user-unit));
  --scale-round-x: 1px;
  --scale-round-y: 1px;
  direction: ltr;
  width: 816px;
  height: 1056px;
  margin: var(--page-margin);
  position: relative;
  overflow: visible;
  border: var(--page-border);
  background-clip: content-box;
  background-color: var(--page-bg-color, rgb(255 255 255));
}

.pdfViewer.removePageBorders .page {
  margin: 0 auto 10px;
  border: none;
}

.textLayer {
  position: absolute;
  text-align: initial;
  inset: 0;
  overflow: clip;
  opacity: 1;
  line-height: 1;
  -webkit-text-size-adjust: none;
  -moz-text-size-adjust: none;
  text-size-adjust: none;
  forced-color-adjust: none;
  transform-origin: 0 0;
  caret-color: CanvasText;
  z-index: 0;

  --min-font-size: 1;
  --text-scale-factor: calc(var(--total-scale-factor) * var(--min-font-size));
  --min-font-size-inv: calc(1 / var(--min-font-size));
}

.textLayer :is(span, br) {
  color: transparent;
  position: absolute;
  white-space: pre;
  cursor: text;
  transform-origin: 0 0;
}

.textLayer > :not(.markedContent),
.textLayer .markedContent span:not(.markedContent) {
  z-index: 1;
  --font-height: 0;
  font-size: calc(var(--text-scale-factor) * var(--font-height));
  --scale-x: 1;
  --rotate: 0deg;
  transform: rotate(var(--rotate)) scaleX(var(--scale-x)) scale(var(--min-font-size-inv));
}

.textLayer .markedContent {
  display: contents;
}

.textLayer span[role='img'] {
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  cursor: default;
}

.textLayer .endOfContent {
  display: block;
  position: absolute;
  inset: 100% 0 0;
  z-index: 0;
  cursor: default;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
}

.textLayer.selecting .endOfContent {
  top: 0;
}

// Rotation support for landscape / rotated pages
[data-main-rotation='90'] {
  transform: rotate(90deg) translateY(-100%);
}
[data-main-rotation='180'] {
  transform: rotate(180deg) translate(-100%, -100%);
}
[data-main-rotation='270'] {
  transform: rotate(270deg) translateX(-100%);
}

.pdf-viewer {
  display: block;
  position: absolute;
  overflow: auto;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  padding: 0 32px 32px 32px;

  // Phones and tablets fit the page to the width (pagesinit), which pdf.js takes without this padding.
  @include screen-below('laptop') {
    padding: 0 0 16px;
  }

  &::-webkit-scrollbar-track {
    background-color: $color-border;
  }

  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: $border-radius-large;
    background-color: $color-normal-pale;
  }

  #viewer {
    min-height: 100%;
  }

  .page {
    margin: 0 auto 16px;
    position: relative;
  }

  .annotationLayer,
  .annotationEditorLayer {
    display: none;
  }

  // Form-review mode: reveal the AcroForm fields pdf.js renders into the
  // annotation layer (read-only — review only displays the filled values).
  // We don't import pdf_viewer.css (see above), so the essential annotation-layer
  // positioning/widget rules are inlined here, scoped to this mode.
  &--show-forms {
    .annotationLayer {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      transform-origin: 0 0;
      pointer-events: none; // read-only: review only displays the filled values

      // pdf.js annotation-layer custom properties.
      --annotation-unfocused-field-background: url("data:image/svg+xml;charset=UTF-8,<svg width='1px' height='1px' xmlns='http://www.w3.org/2000/svg'><rect width='100%' height='100%' style='fill:rgba(0, 54, 255, 0.13);'/></svg>");
      --input-unfocused-border-color: transparent;
      --input-focus-border-color: Highlight;
      --input-focus-outline: 1px solid Canvas;
      --input-hover-border-color: black;
      --input-disabled-border-color: transparent;

      section {
        position: absolute;
        text-align: initial;
        box-sizing: border-box;
        transform-origin: 0 0;
      }

      .textWidgetAnnotation :is(input, textarea),
      .choiceWidgetAnnotation select,
      .buttonWidgetAnnotation:is(.checkBox, .radioButton) input {
        background-image: var(--annotation-unfocused-field-background);
        border: 2px solid var(--input-unfocused-border-color);
        box-sizing: border-box;
        font: calc(9px * var(--total-scale-factor, 1)) sans-serif;
        height: 100%;
        margin: 0;
        padding: 0 2px;
        vertical-align: top;
        width: 100%;
      }

      .textWidgetAnnotation textarea {
        resize: none;
      }

      .buttonWidgetAnnotation.radioButton input {
        border-radius: 50%;
      }

      section svg {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    }
  }

  &__annotations-layer {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
  }

  &__annotation {
    position: absolute;
    background-color: var(--annotation-bg, hsla(196, 100%, 60%, 0.4));
    transition: background-color 200ms ease, opacity 200ms ease;
    border-radius: $border-radius-small;

    &.selected {
      background-color: var(--annotation-bg-selected, hsla(196, 100%, 60%, 0.55));
      transition: background-color 200ms ease;
    }

    &.temporary {
      background-color: var(--annotation-bg-temporary, hsla(197, 18%, 30%, 0.2));
    }
  }

  &__annotation-anchor {
    position: absolute;
    --size: 32px;
    width: 8px;
    height: 15px;
    background: var(--annotation-anchor-bg, $color-text-annotation);
    border-radius: $border-radius-small 0 0 $border-radius-small;

    &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: 100%;
      transform: translate(-50%, -50%) rotate(45deg);
      background: var(--annotation-anchor-bg, $color-text-annotation);
      height: 10px;
      width: 10px;
      border-radius: 0 $border-radius-small 0 0;
    }

    &.selected {
      width: 14px;
      left: -12px !important;
      background: var(--annotation-anchor-bg-selected, $color-text-annotation-hover);
      transition: all 200ms ease;

      &:after {
        background: var(--annotation-anchor-bg-selected, $color-text-annotation-hover);
        transition: all 200ms ease;
      }
    }
  }

  .textLayer {
    ::selection {
      background-color: hsla(197, 18%, 30%, 0.4);
      color: transparent;
    }

    br::selection,
    span.markedContent::selection {
      background: transparent;
    }

    keyword {
      opacity: 0.4;
    }
  }

  &__keyword {
    position: absolute;
    background-color: rgba(rgb(121, 114, 114), 0.4);
    transition: background-color 200ms ease, opacity 200ms ease;
    border-radius: $border-radius-small;
  }

  &__page {
    margin: 0 auto 16px;
    position: relative;

    &:not(.rendered) {
      background: $color-white;
    }
  }
}
</style>
